import { Link } from "react-router-dom";
import { Globe, Search, Users, ShieldCheck, Lock, MessageSquare, ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";
import { BrandLine } from "../components/Brand";

const AUDIENCES = [
  { id: "public", icon: Globe, title: "Anyone with a link", tone: "#00E5FF",
    sees: ["Team fan pages, only for teams whose coach turns them on: record, schedule, roster (name, number, position, class year), season stats and trading cards. Coaches can hide any player.",
      "A player's highlight page, only if a coach creates and shares that link (player info, combine results, highlight film)",
      "Trading card images, only if someone downloads one and shares it", "The sample team on this site, which is fictional"],
    never: ["GPA, height, weight, contact details, notes, messages or anything not listed above"] },
  { id: "recruiters", icon: Search, title: "College recruiters", tone: "#39FF14",
    sees: ["Search results for players on GAMEDAY teams: name, position, class year, height, weight and GPA",
      "Season stats, game logs, shot chart and hot zones, combine results and highlights",
      "Each team's game schedule (practices and coach-only events are hidden)"],
    never: ["Coach notes, attendance, the playbook or other teams' recruiter notes", "Player or parent email addresses"],
    note: "Only signed-in recruiter accounts with an active plan can search." },
  { id: "team", icon: Users, title: "Your team: players and parents", tone: "#F5C542",
    sees: ["The team schedule, games and stats", "Their own profile, development plan (goals and drills) and attendance",
      "Teammates' profiles on the Roster page: stats, shot chart, combine results, highlights and trading card",
      "Plays the coach shares with them. Coaches pick each play's audience: whole team, players and coaches, or coaches only. Parents see whole-team plays."],
    never: ["Coach notes", "Recruiter messages", "A teammate's GPA, recruiting interest, development plan or attendance"] },
  { id: "coaches", icon: ShieldCheck, title: "Coaches and the team owner", tone: "#E5E7EB",
    sees: ["Everything about their own team, including private coach notes, attendance, box scores and shot charts",
      "Messages from recruiters about their players"], never: ["Recruiters' private notes, tags and boards"] },
];

const List = ({ items, ok }) => (
  <ul className="space-y-2">
    {items.map((t) => (
      <li key={t} className="flex gap-2 text-sm text-slate-200">
        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${ok ? "bg-[#39FF14]" : "bg-red-400"}`} />{t}
      </li>
    ))}
  </ul>
);

const AudienceCard = ({ a }) => (
  <section data-testid={`visibility-${a.id}`} className="rounded-3xl border border-[#1E293B] bg-[#0F172A] p-6">
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: `${a.tone}22`, color: a.tone }}><a.icon className="h-5 w-5" /></span>
      <h2 className="font-display text-2xl font-black uppercase text-white">{a.title}</h2>
    </div>
    <p className="mb-2 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Can see</p>
    <List items={a.sees} ok />
    <p className="mb-2 mt-5 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Can't see</p>
    <List items={a.never} />
    {a.note && <p className="mt-4 text-sm text-slate-400">{a.note}</p>}
  </section>
);

const Callout = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid} className="rounded-3xl border border-[#39FF14]/30 bg-[#39FF14]/5 p-6">
    <h2 className="mb-2 flex items-center gap-2 font-display text-2xl font-black uppercase text-white"><Icon className="h-5 w-5 text-[#39FF14]" />{title}</h2>
    <div className="space-y-2 text-sm text-slate-200">{children}</div>
  </section>
);

export default function Visibility() {
  return (
    <div className="min-h-screen bg-[#0A0E1A] text-slate-100" data-testid="visibility-page">
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-5 py-4 sm:px-10">
          <Logo />
          <Link to="/" data-testid="visibility-home-link" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" />Home</Link>
        </div>
      </header>
      <main className="mx-auto max-w-[1100px] space-y-8 px-5 py-12 sm:px-10">
        <div>
          <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Privacy and visibility</p>
          <h1 className="mt-3 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">Who sees what</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300">A plain-language guide for coaches and families: what is public, what recruiters can see, who can contact players and which notes stay private.</p>
          <BrandLine className="mt-4" />
        </div>
        <Callout icon={MessageSquare} title="Who can contact players" testid="visibility-contact">
          <p>Recruiters message a player's <b>coaches</b> through GAMEDAY, never the player or parents directly. Coaches decide what happens next.</p>
          <p>Players don't have a recruiter inbox in GAMEDAY, and player and parent email addresses are not shown to recruiters.</p>
        </Callout>
        <div className="grid gap-6 lg:grid-cols-2">{AUDIENCES.map((a) => <AudienceCard key={a.id} a={a} />)}</div>
        <Callout icon={Lock} title="Notes that stay private" testid="visibility-private">
          <p><b>Coach notes</b> on a player are visible only to that team's coaches and owner.</p>
          <p><b>Recruiter notes, tags and boards</b> are visible only to the recruiter who wrote them.</p>
          <p><b>Saved style alerts</b> and email settings belong to each recruiter and are never shared.</p>
        </Callout>
      </main>
    </div>
  );
}
